/**
 * Saved Prompts Service - Handles CRUD operations for user-created prompts via the Prompt Library API
 */
import { IPublicClientApplication } from '@azure/msal-browser';
import { UserCreatedPrompt } from '../features/promptLibrary/types';

const SAVED_PROMPTS_ENDPOINT = '/nav-api/prompt-library';

function getBaseUrl(): string {
  return process.env.REACT_APP_APIM_BASE_URL || 'https://navigator.ai.3m.com';
}

async function getAccessToken(msalInstance: IPublicClientApplication): Promise<string> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new Error('No authenticated user found');
  }

  const tokenResponse = await msalInstance.acquireTokenSilent({
    scopes: [`${process.env.REACT_APP_CLIENT_ID || '262b88ff-8a8d-49fc-8485-017906bd3c29'}/user_impersonation`],
    account: accounts[0],
  });

  return tokenResponse.accessToken;
}

/**
 * Save a user-created prompt via the Prompt Library API
 */
export async function savePrompt(
  msalInstance: IPublicClientApplication,
  prompt: Omit<UserCreatedPrompt, 'id' | 'createdAt'>
): Promise<UserCreatedPrompt> {
  const accessToken = await getAccessToken(msalInstance);
  const url = `${getBaseUrl()}${SAVED_PROMPTS_ENDPOINT}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
      'Accept': 'application/json',
    },
    body: JSON.stringify(prompt),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to save prompt: ${response.status} ${errorText}`);
  }

  // API returns { prompt_id, status } — reconstruct the full UserCreatedPrompt
  const data: { prompt_id: string; status: string } = await response.json();
  return { ...prompt, id: data.prompt_id };
}

/**
 * Get all saved prompts for the current user from the Prompt Library API
 */
export async function getSavedPrompts(
  msalInstance: IPublicClientApplication
): Promise<UserCreatedPrompt[]> {
  const accessToken = await getAccessToken(msalInstance);
  const url = `${getBaseUrl()}${SAVED_PROMPTS_ENDPOINT}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch saved prompts: ${response.status} ${errorText}`);
  }

  // API returns { items: [{ prompt_id, title, prompt, agentType, category, createdAt }] }
  const data: { items: Array<{ prompt_id: string; title: string; prompt: string; agentType: string; category?: string; createdAt?: string }> } = await response.json();
  return (data.items ?? []).map((item) => ({
    id: item.prompt_id,
    title: item.title,
    prompt: item.prompt,
    agentType: item.agentType as UserCreatedPrompt['agentType'],
    category: item.category,
    createdAt: item.createdAt,
  }));
}

/**
 * Update a user-created prompt via the Prompt Library API
 */
export async function updateSavedPrompt(
  msalInstance: IPublicClientApplication,
  promptId: string,
  prompt: Partial<Omit<UserCreatedPrompt, 'id' | 'createdAt'>>
): Promise<{ prompt_id: string; status: string }> {
  const accessToken = await getAccessToken(msalInstance);
  const url = `${getBaseUrl()}${SAVED_PROMPTS_ENDPOINT}`;

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
      'Accept': 'application/json',
    },
    // prompt_id must be in the body; no ID in the URL
    body: JSON.stringify({ prompt_id: promptId, ...prompt }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update prompt: ${response.status} ${errorText}`);
  }

  return response.json(); // { prompt_id, status: 'updated' }
}

/**
 * Delete a user-created prompt via the Prompt Library API
 */
export async function deleteSavedPrompt(
  msalInstance: IPublicClientApplication,
  promptId: string
): Promise<void> {
  const accessToken = await getAccessToken(msalInstance);
  const url = `${getBaseUrl()}${SAVED_PROMPTS_ENDPOINT}`;

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
      'Accept': 'application/json',
    },
    // prompt_id must be in the body; no ID in the URL
    body: JSON.stringify({ prompt_id: promptId }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to delete prompt: ${response.status} ${errorText}`);
  }
}
